import { App, Button, Space } from 'antd'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAdminSession } from '../../app/session-provider'
import type { AdminOperationAction, AdminRequestInput } from '../../domain/contracts'
import type { AdminDetailRoute, AdminDetailView } from '../../modules/admin-details'
import type { AdminOperationLaunchContext, AdminRowOperation } from '../../modules/admin-row-operations'
import { messageScheduleCancelAction } from '../../modules/admin-row-operations'
import { useAdminOperations } from './admin-operation-provider'
import { SensitiveExportButton } from './sensitive-export-button'

export function AdminDetailActions({ route, id, view, onTaskExport, onMediaUpload }: {
  route: AdminDetailRoute
  id: string
  view: AdminDetailView
  onTaskExport?: (taskId: string) => void | Promise<void>
  onMediaUpload?: () => void
}) {
  const { hasCapability, request } = useAdminSession()
  const { launch } = useAdminOperations()
  const navigate = useNavigate()
  const [taskExporting, setTaskExporting] = useState(false)
  const button = (
    action: string,
    label: string,
    targetId = id,
    capability?: string,
    options: AdminOperationLaunchContext & { targetStatus?: 'PUBLISHED' | 'UNPUBLISHED' | 'ENDED' } = {},
  ) => !capability || hasCapability(capability)
    ? <Button key={`${action}-${label}`} onClick={() => void launch(action, targetId, view, options)}>{label}</Button>
    : null

  const formPageButton = (
    label: string,
    path: string,
    entityId: string,
    capability?: string,
  ) => {
    if (capability && !hasCapability(capability)) return null
    const paramName = path.match(/\$(\w+)/)?.[1] || 'entityId'
    return <Button key={`form-${path}`} onClick={() => void navigate({ to: path as never, params: { [paramName]: entityId } as never })}>{label}</Button>
  }

  const actions: React.ReactNode[] = []
  if (route === 'users') {
    actions.push(
      button('mip.admin.memberships.grant', '补录会员', id, 'memberships.adjust'),
      button('mip.admin.users.update', '编辑资料', id, 'users.fields.edit'),
      button('mip.admin.users.changePrimaryBranch', '变更服务器', id, 'users.fields.edit'),
      button('mip.admin.users.setControl', '访问控制', id, 'users.access.manage'),
      button('mip.admin.roles.set', '设置角色', id, 'roles.change'),
      button('mip.admin.badges.grant', '授予勋章', id, 'badges.manage'),
      button('mip.admin.growth.adjust', '调整成长值', id, 'growth.adjust'),
    )
  }
  else if (route === 'events') {
    const event = record(view.source?.event)
    const eventStatus = String(event.status || '')
    const targetStatus = eventStatus === 'PUBLISHED' ? 'UNPUBLISHED'
      : ['DRAFT', 'UNPUBLISHED'].includes(eventStatus) ? 'PUBLISHED' : null
    if (['DRAFT', 'UNPUBLISHED'].includes(eventStatus)) {
      actions.push(formPageButton('编辑活动', '/events/$eventId/edit', id, 'events.write'))
    }
    if (targetStatus) actions.push(button(
      'mip.admin.events.changeStatus',
      targetStatus === 'PUBLISHED' ? '发布活动' : '下架活动',
      id,
      'events.write',
      { targetStatus },
    ))
    if (eventStatus === 'PUBLISHED') actions.push(button(
      'mip.admin.events.changeStatus', '结束活动', id, 'events.write', { targetStatus: 'ENDED' },
    ))
    if (eventStatus === 'DRAFT') actions.push(button('mip.admin.events.archive', '归档活动', id, 'events.write'))
    actions.push(
      button('mip.admin.events.clone', '克隆活动', id, 'events.write'),
      button('mip.admin.events.tags.replace', '活动标签', id, 'events.write'),
      button('mip.admin.communications.publishEventReminder', '发布提醒', id, 'communications.publish'),
      button('mip.admin.events.participants.import', '补录报名', id, 'events.registrations.manage'),
      <CheckinQrcodeButton key="checkin-qrcode" eventId={id} request={request} hasCapability={hasCapability} />,
    )
    if (hasCapability('exports.create') && hasCapability('events.feedback.read')) actions.push(
      <SensitiveExportButton key="event-feedback-export" kind="eventFeedback" eventId={id} query="" status="" />,
    )
  }
  else if (route === 'orders') actions.push(button('mip.admin.refunds.submit', '提交退款', id, 'refunds.submit'))
  else if (route === 'tasks') {
    const task = record(view.source?.task)
    const status = String(task.status || '')
    const templateUrl = safeMediaUrl(record(task.template).url)
    if (templateUrl) actions.push(<Button key="task-template" href={templateUrl} target="_blank" rel="noopener noreferrer">查看 / 下载模板</Button>)
    const selected = String(task.assignmentMode || '') === 'SELECTED'
    actions.push(formPageButton('编辑任务', '/tasks/$taskId/edit', id, 'tasks.manage'))
    if (['DRAFT', 'UNPUBLISHED'].includes(status)) actions.push(button('mip.admin.tasks.publish', '发布任务', id, 'tasks.manage'))
    if (status === 'PUBLISHED') actions.push(button('mip.admin.tasks.unpublish', '下架任务', id, 'tasks.manage'))
    if (selected) actions.push(
      button('mip.admin.tasks.assignMembers', '分配成员', id, 'tasks.manage'),
      button('mip.admin.tasks.revokeMembers', '撤销成员', id, 'tasks.manage'),
    )
    if (selected) actions.push(button('mip.admin.tasks.assign', '派发任务', id, 'tasks.manage'))
    actions.push(button('mip.admin.tasks.delete', '删除任务', id, 'tasks.manage'))
    if (hasCapability('tasks.manage') && onTaskExport) actions.push(
      <Button
        key="task-export"
        loading={taskExporting}
        onClick={async () => {
          setTaskExporting(true)
          try {
            await onTaskExport(id)
          }
          finally {
            setTaskExporting(false)
          }
        }}
      >
        导出完成记录
      </Button>,
    )
  }
  else if (route === 'taskCompletions') {
    const completion = record(view.source?.completion)
    const submissionId = String(completion.id || completion.submissionId || id)
    const submissionStatus = String(completion.submissionStatus || '')
    const attachmentUrl = safeMediaUrl(record(completion.attachment).url)
    if (attachmentUrl) actions.push(<Button key="task-attachment" href={attachmentUrl} target="_blank" rel="noopener noreferrer">查看 / 下载附件</Button>)
    if (completion.canReview === true && submissionStatus === 'pending_review') {
      actions.push(
        button('mip.admin.tasks.submissions.approve', '通过', submissionId, 'tasks.manage'),
        button('mip.admin.tasks.submissions.reject', '退回', submissionId, 'tasks.manage'),
      )
    }
    else if (completion.canReview === true && submissionStatus === 'reward_failed') {
      actions.push(button('mip.admin.tasks.submissions.retryReward', '重试奖励', submissionId, 'tasks.manage'))
    }
  }
  else if (route === 'banners') {
    const banner = record(view.source?.banner)
    const version = positiveVersion(banner.version)
    const status = String(banner.status || '')
    if (onMediaUpload) actions.push(<Button key="banner-upload" onClick={onMediaUpload}>上传图片</Button>)
    actions.push(button('mip.admin.banners.save', '编辑 Banner', id, 'banners.manage'))
    if (version) {
      if (['ACTIVE', 'INACTIVE'].includes(status)) actions.push(button(
        'mip.admin.banners.changeStatus', status === 'ACTIVE' ? '停用' : '启用', id, 'banners.manage',
        { expectedVersion: version, values: { status: status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' } },
      ))
      actions.push(
        button('mip.admin.banners.move', '上移', id, 'banners.manage', { expectedVersion: version, values: { direction: 'UP' } }),
        button('mip.admin.banners.move', '下移', id, 'banners.manage', { expectedVersion: version, values: { direction: 'DOWN' } }),
      )
      if (status !== 'DELETED') actions.push(button('mip.admin.banners.delete', '删除', id, 'banners.manage', { expectedVersion: version }))
    }
  }
  else if (route === 'gameSeasons') {
    const season = record(view.source?.season)
    const version = positiveVersion(season.version)
    const status = String(season.status || '')
    if (status !== 'CLOSED') actions.push(button('mip.admin.game.seasons.save', '编辑赛季', id, 'game.manage'))
    if (version && ['DRAFT', 'ACTIVE'].includes(status)) actions.push(button(
      'mip.admin.game.seasons.changeStatus', status === 'DRAFT' ? '启用赛季' : '结束赛季', id, 'game.manage',
      { expectedVersion: version, values: { seasonId: id, status: status === 'DRAFT' ? 'ACTIVE' : 'CLOSED' } },
    ))
    actions.push(
      button('mip.admin.game.teams.save', '新增战队', '', 'game.manage'),
      button('mip.admin.game.matches.save', '新增周赛', '', 'game.manage'),
      button('mip.admin.game.rankings.generate', '生成排行', '', 'game.manage'),
    )
  }
  else if (route === 'gameTeams') {
    const team = record(view.source?.team)
    const teamId = String(team.id || id)
    const version = positiveVersion(team.version)
    const status = String(team.status || '')
    const seasonId = String(view.source?.seasonId || team.seasonId || '')
    actions.push(button('mip.admin.game.teams.save', '编辑战队', teamId, 'game.manage'))
    if (version && ['ACTIVE', 'INACTIVE'].includes(status)) actions.push(button(
      'mip.admin.game.teams.changeStatus', status === 'ACTIVE' ? '停用战队' : '启用战队', teamId, 'game.manage',
      { expectedVersion: version, values: { seasonId, teamId, status: status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' } },
    ))
    actions.push(button('mip.admin.game.teams.members.replace', '替换成员', teamId, 'game.manage'))
  }
  else if (route === 'gameCatalogs') {
    const catalog = record(view.source?.catalog)
    const version = positiveVersion(catalog.version)
    const status = String(catalog.status || '')
    actions.push(button('mip.admin.game.blindBoxes.catalogs.save', '编辑目录', id, 'game.manage'))
    if (version && ['PUBLISHED', 'UNPUBLISHED'].includes(status)) actions.push(button(
      'mip.admin.game.blindBoxes.catalogs.changeStatus', status === 'PUBLISHED' ? '下架目录' : '发布目录', id, 'game.manage',
      { expectedVersion: version, values: { catalogId: id, status: status === 'PUBLISHED' ? 'UNPUBLISHED' : 'PUBLISHED' } },
    ))
    actions.push(button('mip.admin.game.blindBoxes.cards.save', '新增卡片', '', 'game.manage'))
  }
  else if (route === 'messages') {
    const source = record(view.source)
    const cancel = messageScheduleCancelAction(record(source.campaign), record(source.dispatch))
    actions.push(
      button('mip.admin.messageCampaigns.save', '编辑消息', id, 'messages.manage'),
      button('mip.admin.messageCampaigns.snapshot', '生成收件人快照', id, 'messages.manage'),
      button('mip.admin.messageCampaigns.schedule', '设置发送时间', id, 'messages.manage'),
      cancel ? button(cancel.action, cancel.label, cancel.targetId, 'messages.manage', cancel) : null,
      button('mip.admin.messageCampaigns.publish', '发布消息', id, 'messages.manage'),
      button('mip.admin.messageCampaigns.withdraw', '撤回消息', id, 'messages.manage'),
    )
  }
  else if (route === 'knowledge') actions.push(
    formPageButton('编辑内容', '/knowledge/$contentId/edit', id, 'knowledge.manage'),
    button('mip.admin.knowledge.contents.review', '审核内容', id, 'knowledge.manage'),
  )
  else if (route === 'opportunities') {
    const opportunity = record(view.source?.opportunity)
    if (!opportunity.deleted && opportunity.status !== 'ARCHIVED') actions.push(
      formPageButton('编辑机会', '/opportunities/$opportunityId/edit', id, 'opportunities.moderate'),
      button('mip.admin.opportunities.publish', '发布机会', id, 'opportunities.moderate'),
      button('mip.admin.opportunities.end', '结束机会', id, 'opportunities.moderate'),
      button('mip.admin.opportunities.unpublish', '下架机会', id, 'opportunities.moderate'),
      button('mip.admin.opportunities.archive', '归档机会', id, 'opportunities.archive'),
      button('mip.admin.opportunities.delete', '删除机会', id, 'opportunities.archive'),
    )
  }
  else if (route === 'userContent') {
    const item = record(view.source?.userContent)
    const contentId = String(item.id || '')
    const kind = String(item.kind || '')
    const status = String(item.status || '')
    const expectedVersion = positiveVersion(item.version)
    const values = expectedVersion && contentId && ['COOPERATION_CARD', 'SUPER_CASE'].includes(kind)
      ? { kind, contentId, expectedVersion }
      : null
    actions.push(formPageButton('编辑内容', '/userContent/$contentId/edit', `${kind}:${contentId}`, 'userContent.moderate'))
    if (values && status === 'PUBLISHED') actions.push(button(
      'mip.admin.userContent.unpublish', '下架内容', contentId, 'userContent.moderate', { values },
    ))
    if (values && status !== 'ARCHIVED') actions.push(button(
      'mip.admin.userContent.archive', '归档内容', contentId, 'userContent.moderate', { values },
    ))
  }

  return actions.some(Boolean) ? <Space size={8} wrap>{actions}</Space> : null
}

export function useDetailRowAction(view: AdminDetailView | null) {
  const { launch } = useAdminOperations()
  return (operation: AdminRowOperation) => launch(
    operation.action,
    operation.targetId,
    view,
    operation,
  )
}

function safeMediaUrl(value: unknown) {
  try {
    const url = new URL(String(value || ''))
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : ''
  }
  catch { return '' }
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function positiveVersion(value: unknown) {
  const version = Number(value)
  return Number.isSafeInteger(version) && version >= 1 ? version : null
}

interface CheckinQrcodeResult {
  qrCodeUrl?: string
  qrCodeDataUrl?: string
  url?: string
}

function CheckinQrcodeButton({ eventId, request, hasCapability }: {
  eventId: string
  request: <T>(action: AdminOperationAction, input?: AdminRequestInput) => Promise<T>
  hasCapability: (capability: string) => boolean
}) {
  const { message } = App.useApp()
  const [loading, setLoading] = useState(false)
  if (!hasCapability('events.write')) return null
  const onClick = async () => {
    setLoading(true)
    try {
      const result = await request<CheckinQrcodeResult>('mip.admin.events.checkinQrcode.get', { eventId })
      const url = result?.qrCodeUrl || result?.qrCodeDataUrl || result?.url || ''
      if (url) window.open(url, '_blank', 'noopener')
      else void message.info('签到二维码暂不可用')
    }
    catch (reason) {
      void message.error(reason instanceof Error ? reason.message : '签到二维码获取失败')
    }
    finally {
      setLoading(false)
    }
  }
  return <Button key="checkin-qrcode" loading={loading} onClick={() => void onClick()}>签到二维码</Button>
}
